/* SalesSparkline.jsx — tiny 6-bar inline chart for 6-month sales velocity.
 *
 * Renders an SVG with 6 vertical bars representing the qty sold per month
 * (oldest → newest, left → right). Heights are scaled to the max value in
 * the series so each row's sparkline is self-relative.
 * Colour-coded by trend: rising (green), flat (slate), falling (amber).
 */
import { useMemo } from "react";

const BAR_WIDTH = 4;
const BAR_GAP = 2;
const HEIGHT = 22;
const PADDING_Y = 1;

export const SalesSparkline = ({ values, monthLabels }) => {
  const safeValues = useMemo(() => {
    if (!Array.isArray(values) || values.length === 0) return [0, 0, 0, 0, 0, 0];
    return values.map(v => Math.max(0, Number(v) || 0));
  }, [values]);

  const total = safeValues.reduce((a, b) => a + b, 0);
  const max = Math.max(...safeValues);
  const count = safeValues.length;
  const width = count * BAR_WIDTH + (count - 1) * BAR_GAP;
  const innerH = HEIGHT - PADDING_Y * 2;

  // Trend: compare the average of the first half vs the second half.
  let trendColor = "#94a3b8"; // slate-400 = flat / no data
  if (total > 0) {
    const half = Math.floor(count / 2);
    const firstHalf = safeValues.slice(0, half).reduce((a, b) => a + b, 0);
    const secondHalf = safeValues.slice(half).reduce((a, b) => a + b, 0);
    if (secondHalf > firstHalf * 1.15) trendColor = "#16a34a";       // green
    else if (firstHalf > secondHalf * 1.15) trendColor = "#d97706";  // amber
  }

  const tooltip = (monthLabels && monthLabels.length === count)
    ? safeValues.map((v, i) => `${monthLabels[i]}: ${v}`).join(" · ")
    : safeValues.map((v, i) => `M${i + 1}: ${v}`).join(" · ");

  if (total === 0) {
    return (
      <span
        className="inline-flex items-center text-[10px] text-gray-300 leading-none"
        style={{ width, height: HEIGHT }}
        title="No sales recorded in the last 6 months"
        data-testid="sparkline-empty"
      >
        — — —
      </span>
    );
  }

  return (
    <svg
      width={width}
      height={HEIGHT}
      viewBox={`0 0 ${width} ${HEIGHT}`}
      role="img"
      aria-label={`Sales velocity: ${tooltip}`}
      style={{ overflow: "visible" }}
      data-testid="sparkline"
    >
      <title>{tooltip}</title>
      {safeValues.map((v, i) => {
        const h = max > 0 ? Math.max(1, (v / max) * innerH) : 1;
        const x = i * (BAR_WIDTH + BAR_GAP);
        const y = HEIGHT - PADDING_Y - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={BAR_WIDTH}
            height={h}
            fill={trendColor}
            rx={0.5}
            opacity={v === 0 ? 0.25 : 1}
          />
        );
      })}
    </svg>
  );
};

export default SalesSparkline;
